import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useOdHandoversApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import HandoverEditorModal from './HandoverEditorModal';

const Container = styled.div``;

export default function OffboardingHandoversPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OD') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(null);

  const {
    fetching, data, pagination, columns: baseColumns,
    getForOffboarding, saveItems, submit, approve, reject,
  } = useOdHandoversApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];
    const open = () => setEditing({ offboardingId: row.offboardingId, personFullName: row.personFullName });

    if (row.status === 'draft' || row.status === 'rejected') {
      // HR fills/edits items on behalf of the leaver, then saves or submits.
      actions.push({ type: 'edit', label: t('offboardingHandover.action.editItems'), onClick: open });
    } else {
      // ready_for_review / approved: let HR review the actual items (read-only).
      actions.push({ type: 'details', label: t('offboardingHandover.action.view'), onClick: open });
    }

    if (row.status === 'ready_for_review') {
      actions.push({
        type: 'approve',
        label: t('offboardingHandover.action.approve'),
        onClick: () => setConfirm({
          message: t('offboardingHandover.confirm.approve', { person: row.personFullName }),
          onYes: () => approve(row.offboardingId, ''),
        }),
      });
      actions.push({
        type: 'reject',
        label: t('offboardingHandover.action.reject'),
        onClick: () => setConfirm({
          message: t('offboardingHandover.confirm.reject', { person: row.personFullName }),
          onYes: () => reject(row.offboardingId, t('offboardingHandover.defaultRejectReason')),
        }),
      });
    }
    return actions;
  }, [t, writable, approve, reject]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('offboardingHandover.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [t, fetching, columns, data, pagination, filterList, handleFilterConfirm, handleDataStateChange])}
      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />
      {editing && (
        <HandoverEditorModal
          offboardingId={editing.offboardingId}
          personFullName={editing.personFullName}
          onClose={() => setEditing(null)}
          getForOffboarding={getForOffboarding}
          saveItems={saveItems}
          submit={submit}
        />
      )}
    </Container>
  );
}
